import { useCallback, useEffect, useRef, useState } from 'react';
import {
  SelectDefaultValue,
  SelectItem,
  SingleSelectProps,
} from '@/components/select/selectBoxTypes';
import {
  getInitialSingleSelection,
  resolveSingleSelection,
} from '@/components/select/singleSelection';

interface SingleSelectState<T> {
  selected: T | null;
  isInitialized: boolean;
}

const useSingleSelectBox = <T extends SelectItem>(
  data: T[] | undefined,
  { value, onSelect }: SingleSelectProps<T>,
  defaultValue: SelectDefaultValue = 'none',
) => {
  const [state, setState] = useState<SingleSelectState<T>>({
    selected: getInitialSingleSelection(value),
    isInitialized: false,
  });
  const stateRef = useRef(state);
  const previousValueRef = useRef(value);
  stateRef.current = state;

  const applySelection = useCallback(
    (selected: T | null, items?: T[]) => {
      setState({ selected, isInitialized: true });
      onSelect(selected, items);
    },
    [onSelect],
  );

  const handleChange = (selected: T | null) => applySelection(selected, data);
  const handleDeselect = () => applySelection(null);

  useEffect(() => {
    const currentState = stateRef.current;
    const hasExternalValueChanged = previousValueRef.current !== value;

    if (hasExternalValueChanged && value === null) {
      previousValueRef.current = value;
      if (currentState.selected) applySelection(null);
      return;
    }

    if (!data) return;
    previousValueRef.current = value;

    if (hasExternalValueChanged || !currentState.isInitialized) {
      const resolvedSelection = resolveSingleSelection(data, value);
      if (resolvedSelection !== null) {
        if (resolvedSelection !== currentState.selected) applySelection(resolvedSelection);
        return;
      }

      if (value !== null && currentState.selected) {
        applySelection(null);
        return;
      }
    }

    if (currentState.isInitialized) return;

    if (defaultValue === 'first' && data.length > 0) {
      applySelection(data[0]);
      return;
    }

    setState((previousState) => ({ ...previousState, isInitialized: true }));
  }, [data, value, defaultValue, applySelection]);

  return { ...state, handleChange, handleDeselect };
};

export default useSingleSelectBox;
